import { type FC, useCallback, useEffect, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { Box, ClickAwayListener, Popper, type SxProps, type Theme } from '@mui/material'

import { useKeyDown } from '@dhl/utils'

import { SearchContextProvider, useSearchContext } from '~/search/model/context.js'
import { SearchBox } from './SearchBox.js'
import { SearchButton } from './search-button/SearchButton.js'
import { SearchResults } from './SearchResults.js'
import { useSearchStore } from '~/search/model/useSearchStore.js'

const classes: Record<string, SxProps<Theme>> = {
  searchResultsContainer: {
    backgroundColor: (theme) => theme.palette.grey[100],
    maxHeight: 560,
    minHeight: 400,
    overflow: 'auto',
    paddingTop: 2,
    '&::-webkit-scrollbar': {
      width: 6,
    },
    '&::-webkit-scrollbar-track': {
      background: `rgba(0, 0, 0, 0.08)`,
      borderRadius: 3,
    },
    '&::-webkit-scrollbar-thumb': {
      background: `rgba(0, 0, 0, 0.1)`,
      borderRadius: 3,
    },
    '&::-webkit-scrollbar-thumb:hover': {
      background: `rgba(0, 0, 0, 0.2)`,
    },
  },
  searchResults: {
    marginTop: -2,
  },
  popover: {
    backgroundColor: (theme) => theme.palette.grey[100],
    border: `1px solid #e7e7e7`,
    borderRadius: 3,
    boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.05);',
    maxHeight: 600,
    minHeight: 400,
    left: '1px !important',
    top: '4px !important',
    width: 343,
    zIndex: (theme) => theme.zIndex.appBar,
  },
}

const DesktopSearchComponent = () => {
  const [isSearching, setSearching] = useState(false)
  const ref = useRef(null)
  const [showResults, setShowResults] = useState(false)
  const escKeyDown = useKeyDown('Escape')
  const { pathname } = useLocation()
  const { setOrigin } = useSearchStore()

  const { searchTerm, clearSearchInput, setSearchTerm } = useSearchContext()

  const cancelSearch = useCallback(() => {
    setSearching(false)
    clearSearchInput()
  }, [clearSearchInput])

  const handleSearchClick = () => setSearching(!isSearching)

  useEffect(() => {
    if (escKeyDown) {
      cancelSearch()
    }
  }, [cancelSearch, escKeyDown, setSearchTerm])

  useEffect(() => setShowResults(!!searchTerm), [searchTerm])

  // Reset the search input upon first load to prevent issues with state state
  // after navigating to another route
  useEffect(() => cancelSearch(), [cancelSearch])

  useEffect(() => {
    setOrigin(pathname)
  }, [pathname, setOrigin])

  return (
    <>
      {!isSearching && <SearchButton onClick={handleSearchClick} />}
      {isSearching && (
        <ClickAwayListener onClickAway={cancelSearch}>
          <div>
            <div ref={ref}>
              <SearchBox
                showSearchIcon
                searchTerm={searchTerm}
                onSearchChange={setSearchTerm}
                onSearchClear={clearSearchInput}
                sx={{
                  width: 343,
                }}
              />
            </div>

            <Popper anchorEl={ref?.current} open={showResults} placement="bottom" sx={classes.popover}>
              <Box sx={classes.searchResultsContainer}>
                <SearchResults sx={classes.searchResults} />
              </Box>
            </Popper>
          </div>
        </ClickAwayListener>
      )}
    </>
  )
}

export const DesktopSearch: FC = () => (
  <SearchContextProvider>
    <DesktopSearchComponent />
  </SearchContextProvider>
)
